import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { classifyCoachUtterance } from '../lib/coachFeedback';

interface ReviewModeResult {
  /** True when the current user is a pro-coach reviewer
   *  (users.coach_profile_extra.is_review_coach === true). */
  isReviewMode: boolean;
  /** True while we're still hydrating the flag. UI banners should
   *  hold off until this lands to avoid a flicker on first paint. */
  loading: boolean;
  /** When the coach finished the first-run tour, or null if they
   *  haven't yet. The /welcome route gates on this. */
  firstRunCompletedAt: string | null;
  /** Coach's display name (from users.name). Used in the first-run
   *  welcome heading. */
  coachName: string | null;
  /** Set `coach_profile_extra.first_run_completed_at` to now. Called
   *  by FirstRunTour when the coach finishes or skips the tour for
   *  the first time. No-op on replay (already set). */
  markFirstRunCompleted: () => Promise<void>;
  /** Log a coach utterance + (optional) Brain reply, then kick off the
   *  async classifier. Fire-and-forget: returns void; the row is
   *  persisted before the classifier patches the kind. Returns the
   *  inserted row id (or null on failure) so callers can correlate
   *  e.g. with an "Undo" toast. */
  logUtterance: (transcript: string, brainReply?: string | null) => Promise<string | null>;
  /** Mark the most recently logged utterance as `manual_feedback`,
   *  bypassing the classifier. Powered by the "Flag this as feedback"
   *  button in the review banner. */
  flagLastAsFeedback: () => Promise<void>;
  /** Log an utterance with a pre-determined kind (skip the async
   *  classifier). Used by `useBrain.send()` for the heuristic-detected
   *  obvious-feedback short-circuit — we already know the kind, no need
   *  to round-trip Haiku. */
  logKnownUtterance: (
    transcript: string,
    kind: 'feedback' | 'action' | 'manual_feedback',
    brainReply?: string | null,
  ) => Promise<string | null>;
  /** Log structured per-item feedback (a reaction + prose) about a
   *  specific drill or session. Reuses `coach_review_log` with
   *  kind='manual_feedback' and the entity_type/entity_id/reaction
   *  columns (migration 00090) so it lands in the same admin feed as the
   *  coach's free voice utterances. Returns the row id or null. */
  logItemFeedback: (args: {
    entityType: 'DRILL' | 'SESSION';
    entityId: string;
    entityName: string;
    reaction: 'works' | 'mixed' | 'change';
    whatWorks: string;
    whatToChange: string;
  }) => Promise<string | null>;
}

const NULL_RESULT: ReviewModeResult = {
  isReviewMode: false,
  loading: false,
  firstRunCompletedAt: null,
  coachName: null,
  markFirstRunCompleted: async () => {},
  logUtterance: async () => null,
  flagLastAsFeedback: async () => {},
  logKnownUtterance: async () => null,
  logItemFeedback: async () => null,
};

/**
 * Hook that detects whether the current user is in pro-coach review
 * mode and exposes the utterance-logging API.
 *
 *  - The flag lives in `users.coach_profile_extra.is_review_coach`
 *    (boolean). Set by the coach-review-redeem edge function on first
 *    redemption. Read here on user-id change.
 *  - `logUtterance` inserts a `coach_review_log` row with `kind='pending'`
 *    and then runs the classifier asynchronously, patching the row when
 *    it returns. RLS allows the coach to insert + update their own rows.
 *  - `flagLastAsFeedback` lets a coach explicitly mark an utterance as
 *    feedback (used by the manual button in the review banner).
 */
export function useCoachReviewMode(): ReviewModeResult {
  const { user } = useAuth();
  const [isReviewMode, setIsReviewMode] = useState(false);
  const [firstRunCompletedAt, setFirstRunCompletedAt] = useState<string | null>(null);
  const [coachName, setCoachName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastLoggedId, setLastLoggedId] = useState<string | null>(null);

  // Hydrate the flag from users.coach_profile_extra on user change.
  useEffect(() => {
    if (!user) {
      setIsReviewMode(false);
      setFirstRunCompletedAt(null);
      setCoachName(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    void (async () => {
      const { data } = await supabase
        .from('users')
        .select('coach_profile_extra, name')
        .eq('id', user.id)
        .maybeSingle();
      if (cancelled) return;
      const extra = (data?.coach_profile_extra ?? null) as { is_review_coach?: boolean; first_run_completed_at?: string } | null;
      setIsReviewMode(!!extra?.is_review_coach);
      setFirstRunCompletedAt(extra?.first_run_completed_at ?? null);
      setCoachName((data?.name as string | undefined) ?? null);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [user]);

  const markFirstRunCompleted = useCallback(async () => {
    if (!user) return;
    // Read current extra, merge in the timestamp, write back. Single
    // write so we don't blow away the rest of coach_profile_extra.
    const { data } = await supabase
      .from('users')
      .select('coach_profile_extra')
      .eq('id', user.id)
      .maybeSingle();
    const extra = (data?.coach_profile_extra ?? {}) as Record<string, unknown>;
    // Don't overwrite an existing timestamp — replay completions
    // shouldn't reset the original first-completion date.
    if (extra.first_run_completed_at) {
      setFirstRunCompletedAt(extra.first_run_completed_at as string);
      return;
    }
    const now = new Date().toISOString();
    const next = { ...extra, first_run_completed_at: now };
    const { error } = await supabase.from('users').update({ coach_profile_extra: next }).eq('id', user.id);
    if (!error) setFirstRunCompletedAt(now);
  }, [user]);

  const logUtterance = useCallback(async (transcript: string, brainReply?: string | null): Promise<string | null> => {
    if (!user || !isReviewMode) return null;
    const clean = transcript.trim();
    if (!clean) return null;
    const route = typeof window !== 'undefined' ? window.location.pathname : '';
    // Insert with kind='pending'. The classifier patches it shortly.
    const { data, error } = await supabase
      .from('coach_review_log')
      .insert({
        coach_id: user.id,
        route,
        transcript: clean,
        kind: 'pending',
        brain_reply: brainReply ?? null,
      })
      .select('id')
      .single();
    if (error || !data) {
      // eslint-disable-next-line no-console
      console.warn('[useCoachReviewMode] log insert failed', error);
      return null;
    }
    const id = (data as { id: string }).id;
    setLastLoggedId(id);
    // Async classify + patch. Doesn't await — the caller doesn't wait.
    void (async () => {
      const result = await classifyCoachUtterance(clean, { route, brainReply: brainReply ?? null });
      if (!result) return;
      const { error: updErr } = await supabase
        .from('coach_review_log')
        .update({
          kind: result.kind,
          confidence: result.confidence,
          feedback_text: result.feedback_text,
          action_intent: result.action_intent,
        })
        .eq('id', id);
      if (updErr) {
        // eslint-disable-next-line no-console
        console.warn('[useCoachReviewMode] classify patch failed', updErr);
      }
    })();
    return id;
  }, [user, isReviewMode]);

  const flagLastAsFeedback = useCallback(async () => {
    if (!user || !lastLoggedId) return;
    const { error } = await supabase
      .from('coach_review_log')
      .update({ kind: 'manual_feedback', confidence: 1.0 })
      .eq('id', lastLoggedId);
    if (error) {
      // eslint-disable-next-line no-console
      console.warn('[useCoachReviewMode] flag-as-feedback failed', error);
    }
  }, [user, lastLoggedId]);

  const logKnownUtterance = useCallback(async (
    transcript: string,
    kind: 'feedback' | 'action' | 'manual_feedback',
    brainReply?: string | null,
  ): Promise<string | null> => {
    if (!user || !isReviewMode) return null;
    const clean = transcript.trim();
    if (!clean) return null;
    const route = typeof window !== 'undefined' ? window.location.pathname : '';
    const { data, error } = await supabase
      .from('coach_review_log')
      .insert({
        coach_id: user.id,
        route,
        transcript: clean,
        kind,
        confidence: 1.0,
        feedback_text: kind === 'action' ? null : clean,
        brain_reply: brainReply ?? null,
      })
      .select('id')
      .single();
    if (error || !data) {
      // eslint-disable-next-line no-console
      console.warn('[useCoachReviewMode] known-utterance log failed', error);
      return null;
    }
    const id = (data as { id: string }).id;
    setLastLoggedId(id);
    return id;
  }, [user, isReviewMode]);

  const logItemFeedback = useCallback<ReviewModeResult['logItemFeedback']>(async ({
    entityType,
    entityId,
    entityName,
    reaction,
    whatWorks,
    whatToChange,
  }): Promise<string | null> => {
    if (!user || !isReviewMode) return null;
    const works = whatWorks.trim();
    const change = whatToChange.trim();
    if (!reaction && !works && !change) return null;
    const REACTION_LABEL: Record<'works' | 'mixed' | 'change', string> = {
      works: '👍 works well',
      mixed: '😐 mixed',
      change: '👎 would change it',
    };
    const noun = entityType === 'DRILL' ? 'drill' : 'session';
    const lines = [`Feedback on ${noun} "${entityName}" — ${REACTION_LABEL[reaction]}.`];
    if (works) lines.push(`Works: ${works}`);
    if (change) lines.push(`Change: ${change}`);
    const feedbackText =
      [works ? `Works: ${works}` : '', change ? `Change: ${change}` : '']
        .filter(Boolean)
        .join(' · ') || REACTION_LABEL[reaction];
    const route = typeof window !== 'undefined' ? window.location.pathname : '';
    const { data, error } = await supabase
      .from('coach_review_log')
      .insert({
        coach_id: user.id,
        route,
        transcript: lines.join('\n'),
        kind: 'manual_feedback',
        confidence: 1.0,
        feedback_text: feedbackText,
        entity_type: entityType,
        entity_id: entityId,
        reaction,
      })
      .select('id')
      .single();
    if (error || !data) {
      console.warn('[useCoachReviewMode] item feedback insert failed', error);
      return null;
    }
    const id = (data as { id: string }).id;
    setLastLoggedId(id);
    return id;
  }, [user, isReviewMode]);

  // CRITICAL: memoise the return value. Without this, every render produces
  // a new object literal which destabilises `reviewMode` as a dep in
  // useBrain.send + useBrainAutoSpeak — causing those hooks' useCallbacks
  // and useEffects to re-fire every render, which cascades into a setTurns
  // loop and "Maximum update depth exceeded".
  return useMemo(() => {
    if (!isReviewMode && !loading) return NULL_RESULT;
    return {
      isReviewMode,
      loading,
      firstRunCompletedAt,
      coachName,
      markFirstRunCompleted,
      logUtterance,
      flagLastAsFeedback,
      logKnownUtterance,
      logItemFeedback,
    };
  }, [isReviewMode, loading, firstRunCompletedAt, coachName, markFirstRunCompleted, logUtterance, flagLastAsFeedback, logKnownUtterance, logItemFeedback]);
}
